<main class="canvas-wide">
  <!-- Page header (AC1) -->
  <div class="release-page-head">
    <div>
      <h1>Releases</h1>
      <p class="page-sub">Pick a release to enter or review historical results.</p>
    </div>
  </div>

  @if (isLoading()) {
    <!-- Skeleton placeholder that mirrors the loaded layout (AC: polished loading) -->
    <div class="releases-skeleton" aria-busy="true" aria-label="Loading releases">
      <div class="skel-hero">
        <span class="skel skel-eyebrow"></span>
        <span class="skel skel-title"></span>
        <span class="skel skel-desc"></span>
        <span class="skel skel-bar"></span>
        <div class="skel-stats">
          <span class="skel skel-stat"></span>
          <span class="skel skel-stat"></span>
          <span class="skel skel-stat"></span>
          <span class="skel skel-stat"></span>
        </div>
        <span class="skel skel-cta"></span>
      </div>

      <span class="skel skel-toolbar"></span>

      @for (row of skeletonRows; track row) {
        <div class="skel-row">
          <div class="skel-row-main">
            <span class="skel skel-line skel-line-lg"></span>
            <span class="skel skel-line skel-line-sm"></span>
          </div>
          <span class="skel skel-pill"></span>
        </div>
      }
    </div>
  } @else if (loadError()) {
    <!-- Failure state: the request came back with an error (AC: loading then failure) -->
    <div class="error-state">
      <div class="error-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <h4>We couldn't load your releases</h4>
      <p>The server didn't respond. Check your connection and try again.</p>
      <button type="button" class="btn btn-primary btn-sm" (click)="reload()">Try again</button>
    </div>
  } @else {
    <!-- Active release heroes, earliest start date first (AC2) -->
    @if (activeReleases().length) {
      <div class="active-releases">
        @for (active of activeReleases(); track active.releaseId) {
          <section class="active-release">
            <div class="eyebrow">Currently active</div>
            <h2>{{ active.releaseName }}</h2>
            <div class="desc">
              Opened {{ startDate(active) | date: 'MMM d' }} · {{ active.numberOfTesters }} testers ·
              {{ active.numberOfTestPlans }} plans · {{ active.numberOfTests }} tests
            </div>

            <div class="progress-row">
              <div class="progress-bar">
                <div class="fill" [style.width.%]="completedPct(active)"></div>
                <div
                  class="fail-fill"
                  [style.left.%]="passingPct(active)"
                  [style.width.%]="failingPct(active)"
                ></div>
              </div>
              <div class="progress-label">{{ completedPct(active) | number: '1.0-0' }}%</div>
            </div>

            <div class="active-stats">
              <div>
                <div class="stat-lbl">Passed</div>
                <div class="stat-val">{{ active.numberOfPassingTests }}</div>
              </div>
              <div>
                <div class="stat-lbl">Failed</div>
                <div class="stat-val accent">{{ active.numberOfFailingTests }}</div>
              </div>
              <div>
                <div class="stat-lbl">Remaining</div>
                <div class="stat-val">{{ remaining(active) }}</div>
              </div>
              <div>
                <div class="stat-lbl">Showstoppers</div>
                <div class="stat-val" [class.accent]="active.numberOfShowstoppers > 0">
                  {{ active.numberOfShowstoppers }}
                </div>
              </div>
            </div>

            <div class="actions">
              <a
                [routerLink]="['/releases', active.releaseId]"
                class="btn btn-on-dark btn-lg"
                style="min-width: 0"
              >
                Enter release
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </a>
              <!-- Carry the release id so Insights opens this card's release, not
                   whichever active one it would default to. -->
              <a
                routerLink="/insights"
                [queryParams]="{ releaseId: active.releaseId }"
                class="btn btn-outline-on-dark"
                >View insights</a
              >
            </div>
          </section>
        }
      </div>
    }

    <!-- Search + status filter chips (AC3) -->
    <div class="toolbar">
      <div class="search-input">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          type="text"
          placeholder="Search releases"
          [value]="searchTerm()"
          (input)="searchTerm.set($any($event.target).value)"
          aria-label="Search releases"
        />
        @if (searchTerm()) {
          <button
            type="button"
            class="search-clear"
            aria-label="Clear search"
            title="Clear search"
            (click)="searchTerm.set('')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        }
      </div>
      <div class="chip-group">
        @for (chip of statusChips; track chip) {
          <button
            type="button"
            class="chip"
            [class.active]="statusFilter() === chip"
            (click)="statusFilter.set(chip)"
          >
            {{ chip }} <span class="count">{{ statusCounts()[chip] }}</span>
          </button>
        }
      </div>
    </div>

    <!-- Past releases (AC4, AC9) -->
    <section class="history-section">
      <h3>Past releases</h3>

      @if (pastReleases().length === 0) {
        <div class="empty-row">No past releases yet.</div>
      } @else if (filteredPastReleases().length === 0) {
        <!-- Search / filter matched nothing (AC: polished empty state) -->
        <div class="empty-state">
          <div class="empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </div>
          @if (searchTerm()) {
            <h4>No releases match "{{ searchTerm() }}"</h4>
            <p>Try a different name or clear the filters to see everything.</p>
          } @else {
            <h4>No {{ statusFilter() | lowercase }} releases</h4>
            <p>Nothing here with that status. Try another filter.</p>
          }
          <button type="button" class="btn btn-ghost btn-sm" (click)="resetFilters()">
            Clear filters
          </button>
        </div>
      } @else {
        @for (r of filteredPastReleases(); track r.releaseId) {
          <a [routerLink]="['/releases', r.releaseId]" class="release-row">
            <div class="rinfo">
              <div class="rname">{{ r.releaseName }}</div>
              <div class="rdate">
                {{ r.createdOnUtc | date: 'MMM d' }}
                @if (r.closedOnUtc) {
                  to {{ r.closedOnUtc | date: 'MMM d, y' }}
                }
                · {{ r.numberOfTesters }} testers · {{ r.numberOfTestPlans }} plans
              </div>
            </div>

            <div class="mini-stat">
              <span class="lbl">Tests run</span>
              <span class="val">{{ testsRun(r) }}</span>
            </div>
            <div class="mini-stat">
              <span class="lbl">Pass rate</span>
              <span class="val" [class.warn]="passRate(r) < warnThreshold">
                {{ passRate(r) | number: '1.0-1' }}%
              </span>
            </div>
            <div class="mini-stat">
              <span class="lbl">Failures</span>
              <span class="val" [class.fail]="r.numberOfFailingTests > 0">{{
                r.numberOfFailingTests
              }}</span>
            </div>

            <!-- Status pill (AC5) -->
            @switch (r.status) {
              @case ('Shipped') {
                <span class="release-status shipped">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3"
                    width="10"
                    height="10"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Shipped
                </span>
              }
              @case ('Held') {
                <span class="release-status held">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10">
                    <path d="M12 2 1 21h22L12 2zm-1 9v5h2v-5h-2zm0 6v2h2v-2h-2z" />
                  </svg>
                  Held
                </span>
              }
              @case ('Archived') {
                <span class="release-status archived">Archived</span>
              }
            }
          </a>
        }
      }
    </section>
  }
</main>
